import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { ClipboardList, Trash2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'
import { useQuoteList } from '@/components/generals/quote-list-provider'
import { QUOTE_LIST_LIMIT } from '@/constants/quote-form'
import { QUOTE_PATH } from '@/constants/site'

export const QuoteListPanel: FC = (): ReactNode => {
    const router = useRouter()
    const { items, remove, clear, isOpen, setOpen } = useQuoteList()
    const closeRef = useRef<HTMLButtonElement>(null)
    const onQuotePage = router.pathname === QUOTE_PATH

    useEffect(() => {
        if (!isOpen) return

        closeRef.current?.focus()

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', handleKey)

        return () => window.removeEventListener('keydown', handleKey)
    }, [isOpen, setOpen])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router, setOpen])

    useEffect(() => {
        if (items.length === 0) setOpen(false)
    }, [items.length, setOpen])

    const showTrigger = items.length > 0 && !onQuotePage

    return (
        <>
            <button
                type='button'
                onClick={() => setOpen(true)}
                aria-label={`Open your quote list, ${items.length} of ${QUOTE_LIST_LIMIT} items`}
                className={cn(
                    'fixed bottom-[5.25rem] right-4 z-40 flex items-center gap-3 rounded-full bg-cream py-2 pl-2 pr-5 text-sm font-medium text-ink shadow-[0_20px_50px_-20px_rgba(21,23,26,0.55)] ring-1 ring-ink/10 transition-[transform,translate,scale,rotate,opacity] duration-500 ease-expo md:bottom-6 md:right-6',
                    showTrigger ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-6 opacity-0'
                )}
            >
                <span className='flex size-9 items-center justify-center rounded-full bg-ink text-cream'>
                    <ClipboardList className='size-4' aria-hidden='true' />
                </span>

                Your list

                <span className='flex h-6 min-w-6 items-center justify-center rounded-full bg-ember px-1.5 font-mono text-[11px] text-white'>
                    {items.length}
                </span>
            </button>

            <div
                className={cn('fixed inset-0 z-[80] transition-[visibility] duration-500', isOpen ? 'visible' : 'invisible')}
                role='dialog'
                aria-modal='true'
                aria-label="Your quote list"
            >
                <button
                    type='button'
                    tabIndex={-1}
                    aria-hidden='true'
                    onClick={() => setOpen(false)}
                    className={cn('absolute inset-0 bg-ink/40 backdrop-blur-sm transition-opacity duration-500', isOpen ? 'opacity-100' : 'opacity-0')}
                />

                <div
                    className={cn(
                        'absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-paper shadow-2xl transition-transform duration-500 ease-expo',
                        isOpen ? 'translate-x-0' : 'translate-x-full'
                    )}
                >
                    <div className='flex items-center justify-between border-b border-line px-6 py-5'>
                        <div>
                            <p className='font-heading text-2xl font-semibold tracking-[-0.03em]'>
                                Your quote list
                            </p>

                            <p className='mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-slate'>
                                {items.length} of {QUOTE_LIST_LIMIT} slots used · saved in this tab only
                            </p>
                        </div>

                        <button
                            ref={closeRef}
                            type='button'
                            onClick={() => setOpen(false)}
                            aria-label="Close quote list"
                            className='flex size-10 items-center justify-center rounded-full border border-line transition-colors hover:bg-sand'
                        >
                            <X className='size-4' />
                        </button>
                    </div>

                    <ul className='flex-1 space-y-3 overflow-y-auto px-6 py-6'>
                        {items.map(item => (
                            <li key={item.key} className='flex items-center gap-4 rounded-2xl border border-line bg-card p-3'>
                                <span className='relative size-16 shrink-0 overflow-hidden rounded-xl bg-sand'>
                                    {item.image ? (
                                        <Image src={item.image} alt='' fill sizes='64px' className='object-cover' />
                                    ) : (
                                        <span className='flex size-full items-center justify-center font-mono text-[10px] uppercase tracking-[0.12em] text-slate'>
                                            Est.
                                        </span>
                                    )}
                                </span>

                                <span className='min-w-0 flex-1'>
                                    <span className='block truncate font-medium'>{item.title}</span>
                                    <span className='mt-0.5 block truncate text-sm text-slate'>{item.detail}</span>

                                    {item.priceLabel && (
                                        <span className='mt-1 block font-mono text-[12px] text-ink'>{item.priceLabel}</span>
                                    )}
                                </span>

                                <button
                                    type='button'
                                    onClick={() => remove(item.key)}
                                    aria-label={`Remove ${item.title} from your quote list`}
                                    className='flex size-9 shrink-0 items-center justify-center rounded-full text-slate transition-colors hover:bg-sand hover:text-ink'
                                >
                                    <Trash2 className='size-4' />
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className='space-y-3 border-t border-line px-6 py-6'>
                        <ButtonLink href={`${QUOTE_PATH}?entry=quote_list`} size='lg' className='w-full justify-between'>
                            {items.length === 1 ? "Price this item" : `Price all ${items.length} items together`}
                        </ButtonLink>

                        <button
                            type='button'
                            onClick={clear}
                            className='w-full rounded-full py-2 text-sm text-slate transition-colors hover:text-ink'
                        >
                            Remove everything
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}
